"use client";

import { c as _c } from "react/compiler-runtime";
import "maplibre-gl/dist/maplibre-gl.css";
import { Directions, Refresh } from "@mui/icons-material";
import Image from "next/image";
import React, { useCallback, useMemo, useState } from "react";
import Map, { Marker, Popup } from "react-map-gl/maplibre";
import styled from "styled-components";
import { Button, ButtonSize, ButtonVariant, Input, InputIcon, Text, TextColor, TextSize, TextVariant, TextWeight } from "../../components";
import { useMatchBreakpoints } from "../../hooks";
import CarryPin from "./pin.png";
const ShopCardSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-[110px] p-4 border border-gray-200 animate-pulse"><div className="h-5 bg-gray-200 rounded mb-2 w-3/4" /><div className="h-4 bg-gray-200 rounded mb-1 w-full" /><div className="h-4 bg-gray-200 rounded w-2/3" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const MapSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="w-full h-[600px] bg-gray-200 animate-pulse flex items-center justify-center"><div className="text-center"><div className="w-16 h-16 bg-gray-300 rounded-full mx-auto mb-4" /><div className="h-4 bg-gray-300 rounded w-32 mx-auto mb-2" /><div className="h-3 bg-gray-300 rounded w-24 mx-auto" /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const CarryShops = ({
  shops,
  loadingMap = false
}) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedShop, setSelectedShop] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const {
    isXs,
    isSm,
    isMd
  } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd;

  // Domyślne ustawienia mapy
  const defaultViewState = {
    longitude: 18.9252,
    latitude: 52.1,
    zoom: 5.2
  };
  const [viewState, setViewState] = useState(defaultViewState);
  const itemsPerPage = isMobile ? 3 : 9;

  // Filtrowane sklepy na podstawie wyszukiwania
  const filteredShops = useMemo(() => {
    if (!search.trim()) return shops;
    const searchTerm = search.toLowerCase().trim();
    return shops.filter(shop => shop.miasto.toLowerCase().includes(searchTerm) || shop.adres.toLowerCase().includes(searchTerm) || shop.kodPocztowy.includes(searchTerm) || shop.wojewodztwo.toLowerCase().includes(searchTerm));
  }, [shops, search]);

  // Paginacja na przefiltrowanych sklepach
  const paginatedShops = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredShops.slice(startIndex, endIndex);
  }, [filteredShops, currentPage, itemsPerPage]);
  const totalPages = Math.ceil(filteredShops.length / itemsPerPage);
  const handleMarkerClick = useCallback(shop_0 => {
    setSelectedShop(shop_0);
    // Wyśrodkuj mapę na wybranym sklepie
    setViewState(prevState => ({
      ...prevState,
      longitude: Number(shop_0.lng),
      latitude: Number(shop_0.lat) + 0.05,
      // Lekkie przesunięcie w górę dla popup-a
      zoom: Math.max(prevState.zoom || 10, 10) // Ustaw minimum zoom 10 dla lepszej widoczności
    }));
  }, []);
  const handleMapMove = useCallback(evt => {
    setViewState(evt.viewState);
  }, []);
  const handlePageChange = page => {
    setLoadingList(true);
    setTimeout(() => {
      setCurrentPage(page);
      setLoadingList(false);
    }, 500);
  };
  const handleSearchChange = e => {
    setLoadingList(true);
    setSearch(e.target.value);
    setTimeout(() => {
      setCurrentPage(1); // Reset do pierwszej strony przy nowym wyszukiwaniu
      setLoadingList(false);
    }, 500);
  };

  // Funkcja resetowania
  const handleReset = () => {
    setSearch("");
    setCurrentPage(1);
    setSelectedShop(null);
    setViewState(defaultViewState);
  };

  // Sprawdź czy są jakieś filtry aktywne
  const hasActiveFilters = search.trim() !== "" || selectedShop !== null || viewState.longitude !== defaultViewState.longitude || viewState.latitude !== defaultViewState.latitude || viewState.zoom !== defaultViewState.zoom;
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i_0 = 1; i_0 <= 4; i_0++) {
          pages.push(i_0);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i_1 = totalPages - 3; i_1 <= totalPages; i_1++) {
          pages.push(i_1);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i_2 = currentPage - 1; i_2 <= currentPage + 1; i_2++) {
          pages.push(i_2);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };
  if (!shops || shops.length === 0) return null;
  return <div className="w-full max-w-screen-2xl mx-auto h-[calc(100%-37px)] py-8">
      <div className="flex justify-between items-center mb-8 flex-col md:flex-row gap-4">
        <Text text="Mapa salonów Carry" variant={TextVariant.H5} weight={TextWeight.BOLD} />

        {/* Przycisk Reset - pokazuje się tylko gdy są aktywne filtry */}
        {hasActiveFilters && <Button text="Resetuj" variant={ButtonVariant.SECONDARY} size={ButtonSize.S} startIcon={<Refresh />} onClick={handleReset} className="transition-all duration-300 ease-in-out" />}
      </div>

      {/* Mapa lub skeleton */}
      {loadingMap ? <MapSkeleton /> : <Map {...viewState} onMove={handleMapMove} style={{
      width: "100%",
      height: isMobile ? "500px" : "600px"
    }} mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json">
          {/* Renderowanie markerów dla przefiltrowanych sklepów */}
          {filteredShops.map(shop_1 => <Marker key={shop_1.id} longitude={Number(shop_1.lng)} latitude={Number(shop_1.lat)} anchor="bottom" onClick={() => handleMarkerClick(shop_1)}>
              <Image src={CarryPin} alt="Shop marker" width={20} height={40} style={{
          cursor: "pointer"
        }} />
            </Marker>)}

          {/* Popup pokazujący się po kliknięciu w marker */}
          {selectedShop && <StyledPopup longitude={Number(selectedShop.lng)} latitude={Number(selectedShop.lat)} onClose={() => setSelectedShop(null)} closeButton={true} closeOnClick={false} anchor="bottom" offset={[0, -30]}>
              <div className="p-3 max-w-xs">
                <Text text={`Carry ${selectedShop.miasto}`} fontSize={TextSize.S} className="mb-2 text-center w-full" />
                <Text text="Adres" fontSize={TextSize.XS} className="mb-2 text-center w-full" />
                <Text text={`${selectedShop.adres}`} fontSize={TextSize.XS} className="text-center w-full" />
                <Text text={`${selectedShop.kodPocztowy} ${selectedShop.miasto}`} fontSize={TextSize.XS} className="mb-2 text-center w-full" />

                <Text text="Godziny otwarcia" fontSize={TextSize.XS} className="mb-2 text-center w-full" />
                <div className="flex gap-4 justify-between">
                  <Text text="Poniedziałek - Piątek:" fontSize={TextSize.XS} weight={TextWeight.BOLD} />
                  <Text text={`${selectedShop.tydzStart} - ${selectedShop.tydzStop}`} fontSize={TextSize.XS} />
                </div>
                <div className="flex gap-4 justify-between">
                  <Text text="Sobota:" fontSize={TextSize.XS} weight={TextWeight.BOLD} />

                  <Text text={`${selectedShop.sobStart} - ${selectedShop.sobStop}`} fontSize={TextSize.XS} />
                </div>
                <div className="flex gap-4 justify-between">
                  <Text text="Niedziela:" fontSize={TextSize.XS} weight={TextWeight.BOLD} />
                  <Text text={`${selectedShop.ndStart} - ${selectedShop.ndStop}`} fontSize={TextSize.XS} />
                </div>

                <Button text="Nawiguj do sklepu" variant={ButtonVariant.PRIMARY} size={ButtonSize.XS} startIcon={<Directions />} className="!w-full mt-8" onClick={() => {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedShop.lat},${selectedShop.lng}`, "_blank");
          }} />
              </div>
            </StyledPopup>}
        </Map>}

      <div className="flex justify-between items-center my-8 flex-col md:flex-row gap-4">
        <Text text="Lista sklepów" variant={TextVariant.H5} weight={TextWeight.BOLD} />

        <Input id="search" name="search" value={search} icon={true} iconPosition="right" iconBackground={true} iconGreyColored={true} iconVariant={InputIcon.SEARCH} placeholder="Wyszukaj sklep" onChange={handleSearchChange} />
      </div>

      {/* Komunikat gdy brak wyników */}
      {filteredShops.length === 0 && search.trim() && <div className="text-center py-8">
          <Text text={`Nie znaleziono sklepów dla frazy "${search}"`} fontSize={TextSize.M} className="text-gray-500" />
          <Text text="Spróbuj wyszukać po nazwie miasta, adresie lub kodzie pocztowym" fontSize={TextSize.S} className="text-gray-400 mt-2" />
        </div>}

      {/* Lista sklepów */}
      {loadingList ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {Array.from({
        length: itemsPerPage
      }).map((_, index) => <ShopCardSkeleton key={`skeleton-${index}`} />)}
        </div> : paginatedShops.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {paginatedShops.map(shop_2 => <div key={shop_2.id} className="h-[110px] p-4 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer flex items-center justify-between" onClick={() => handleMarkerClick(shop_2)}>
                <div className="flex flex-col justify-between h-full">
                  <Text text={`Carry ${shop_2.miasto}`} fontSize={TextSize.S} weight={TextWeight.BOLD} className="mb-2" />
                  <Text text={shop_2.adres} fontSize={TextSize.XS} className="text-gray-600 mb-1" />
                  <Text text={`${shop_2.kodPocztowy} ${shop_2.miasto}`} fontSize={TextSize.XS} className="text-gray-600" />
                </div>

                <div className="flex flex-col items-center justify-start h-full">
                  <Directions onClick={e_0 => {
            e_0.stopPropagation(); // Zapobiega wywołaniu handleMarkerClick
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${shop_2.lat},${shop_2.lng}`, "_blank");
          }} className="cursor-pointer hover:text-gray-500 transition-colors" />
                </div>
              </div>)}
          </div>}

      {/* Paginacja */}
      {totalPages > 1 && <div className="flex justify-center items-center space-x-2 flex-col md:flex-row gap-4">
          <Text text="Poprzednia" fontSize={TextSize.XS} color={TextColor.GRAYSCALE_500} onClick={() => {
        if (currentPage === 1) return;
        handlePageChange(currentPage - 1);
      }} className={`!h-8  px-2 flex items-center justify-center transition-all duration-200 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50 rounded"}`} />

          <div className="flex items-center gap-2">
            {/* Numery stron */}
            {getPageNumbers().map((page_0, index_0) => <React.Fragment key={index_0}>
                {page_0 === "..." ? <span className="px-3 py-2 text-sm font-medium text-gray-700">
                    ...
                  </span> : <button onClick={() => handlePageChange(page_0)} className={`w-8 h-8 px-3 flex items-center justify-center text-sm font-medium border transition-all duration-200 ${currentPage === page_0 ? "bg-gray-50 border-gray-800 text-gray-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
                    {page_0}
                  </button>}
              </React.Fragment>)}
          </div>

          {/* Przycisk Następna */}
          {currentPage < totalPages && <Text text="Następna" fontSize={TextSize.XS} color={TextColor.GRAYSCALE_500} onClick={() => {
        if (currentPage === totalPages) return;
        handlePageChange(currentPage + 1);
      }} className={`!h-8 px-2 flex items-center justify-center transition-all duration-200 ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50 rounded"}`} />}
        </div>}
    </div>;
};
const StyledPopup = styled(Popup)`
  max-width: 280px !important;

  .maplibregl-popup-close-button {
    padding: 0px 5px;
  }
`;